import React from "react";
import { Link } from "react-router-dom";
import {
    Waves,
    Wind,
    HeartHandshake,
    Activity,
    HomeIcon,
    Briefcase,
    Building2,
    Phone,
    ArrowRight,
} from "lucide-react";
import { COMPANY, DISCLAIMERS } from "@/lib/compliance";
import EmailContact from "@/components/EmailContact";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const img = (file) => `${BACKEND}/api/static/${file}`;

const GENERIC_DISCLOSURE =
    "Coverage availability, eligibility, deductibles, exclusions, endorsements, and premiums vary by carrier, underwriting, policy form, and state. Some perils may require separate policies, endorsements, or carrier approvals.";

const LINES = [
    {
        id: "coastal",
        kicker: "Coastal & Waterfront",
        title: "Flood & Hurricane Coverage Options",
        body:
            "We help coastal homeowners explore flood, wind, and named-storm coverage options. Specific terms, deductibles, and carrier eligibility apply — and these coverages may require separate policies or endorsements.",
        icon: Waves,
        image: img("florida.png"),
        coupleImage: img("florida_couple.png"),
        coupleCaption: "The S. Family — Illustrative",
        tags: [
            "Flood Insurance",
            "Hurricane & Wind",
            "Storm Surge",
            "Waterfront Homes",
        ],
        disclosure: GENERIC_DISCLOSURE,
        testId: "line-coastal",
        pageLink: "/extreme-weather",
    },
    {
        id: "severe",
        kicker: "Plains & Homestead",
        title: "Tornado, Earthquake & Severe-Weather Options",
        body:
            "We help homeowners explore options for tornado, earthquake, hail, wildfire, and severe-thunderstorm coverages. Some perils are commonly excluded from base policies and may require separate endorsements or carrier approvals.",
        icon: Wind,
        image: img("texas.png"),
        coupleImage: img("texas_couple.png"),
        coupleCaption: "The H. Family — Illustrative",
        tags: [
            "Tornado Coverage",
            "Earthquake Protection",
            "Hail & Wind",
            "Wildfire",
            "Homestead Policies",
        ],
        disclosure: GENERIC_DISCLOSURE,
        testId: "line-severe",
        pageLink: "/extreme-weather",
    },
    {
        id: "life",
        kicker: "Family & Legacy",
        title: "Life Insurance Options",
        body:
            "Term, whole, and universal life options to support the people you love. Eligibility, underwriting, medical questions, and product availability vary by carrier and state — we help you compare and choose.",
        icon: HeartHandshake,
        image: img("life.png"),
        coupleImage: img("life_couple.png"),
        coupleCaption: "The M. Family — Illustrative",
        tags: ["Term Life", "Whole Life", "Universal Life", "Final Expense"],
        disclosure: GENERIC_DISCLOSURE,
        testId: "line-life",
        pageLink: "/life-insurance",
    },
    {
        id: "health",
        kicker: "Wellness & Care",
        title: "Health Coverage Options",
        body:
            "Individual, family, and supplemental health coverage options. Plan availability, networks, premiums, deductibles, and benefits vary by carrier, plan year, and state.",
        icon: Activity,
        image: img("health.png"),
        coupleImage: img("health_couple.png"),
        coupleCaption: "The R. Family — Illustrative",
        tags: ["Individual & Family", "Supplemental", "Dental & Vision", "Medicare-eligible"],
        disclosure: GENERIC_DISCLOSURE,
        testId: "line-health",
        pageLink: "/health-insurance",
    },
    {
        id: "property",
        kicker: "Home & Real Property",
        title: "Property Insurance Options",
        body:
            "Homeowners, renters, condo, and dwelling-fire policies for a range of property types. Replacement-cost approach, deductibles, and exclusions are reviewed line by line so you understand what is, and is not, covered.",
        icon: HomeIcon,
        image: img("property.png"),
        coupleImage: img("property_couple.png"),
        coupleCaption: "The W. Family — Illustrative",
        tags: ["Homeowners (HO)", "Renters", "Condo (HO-6)", "Dwelling Fire (DP)"],
        disclosure: GENERIC_DISCLOSURE,
        testId: "line-property",
        pageLink: "/personal-insurance",
    },
    {
        id: "business",
        kicker: "Owners & Operators",
        title: "Business Insurance Options",
        body:
            "General liability, business owner's policies (BOP), commercial property, and workers' compensation options for small and mid-sized operators. Coverage requirements vary by industry, payroll, and jurisdiction.",
        icon: Briefcase,
        image: img("business.png"),
        coupleImage: img("business_couple.png"),
        coupleCaption: "The B. Family — Illustrative",
        tags: ["BOP", "General Liability", "Commercial Property", "Workers' Comp"],
        disclosure: GENERIC_DISCLOSURE,
        testId: "line-business",
        pageLink: "/business-insurance",
    },
    {
        id: "commercial",
        kicker: "Commercial Lines & General Agency",
        title: "D&O, E&O, Bonds, CGL & Commercial Lines",
        body:
            "Through our brokerage and general-agency relationships, we place a wide range of commercial lines: Directors & Officers (D&O), Errors & Omissions (E&O), surety and fidelity bonds, Commercial General Liability (CGL), Business Owner's Policies (BOPs), and other specialty commercial coverages. Eligibility, limits, retentions, and exclusions vary by industry, risk profile, carrier, and state.",
        icon: Building2,
        image: img("commercial.png"),
        coupleImage: img("commercial_couple.png"),
        coupleCaption: "The V. Partners — Illustrative",
        tags: [
            "D&O",
            "E&O",
            "Surety & Fidelity Bonds",
            "CGL",
            "BOP",
            "Specialty Commercial",
        ],
        disclosure: GENERIC_DISCLOSURE,
        testId: "line-commercial",
        pageLink: "/commercial-lines",
        wide: true, // span both columns to balance odd count
    },
];

export default function RegionalCoverage() {
    return (
        <section
            data-testid="regional-coverage"
            className="relative border-t border-[rgba(212,175,55,0.12)] bg-[#07090c] py-16 md:py-24"
        >
            <div className="mx-auto max-w-[1400px] px-6 md:px-10">
                {/* Section header */}
                <div className="mb-12 flex flex-col gap-6 md:mb-16 md:flex-row md:items-end md:justify-between">
                    <div className="max-w-2xl">
                        <p className="text-xs uppercase tracking-[0.4em] text-brand-goldSoft">
                            Lines of Business
                        </p>
                        <h2 className="mt-4 font-serif text-4xl text-brand-ivory sm:text-5xl md:text-6xl">
                            Coverage{" "}
                            <span className="gold-text">options</span>
                            <span className="text-brand-ivory">
                                , line&nbsp;by&nbsp;line.
                            </span>
                        </h2>
                    </div>
                    <p className="max-w-md text-pretty text-base text-brand-muted md:text-right">
                        We help individuals, families, and business owners
                        explore policies suited to their needs and the risks
                        common in their&nbsp;state.
                    </p>
                </div>

                {/* Cards grid */}
                <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-10">
                    {LINES.map(
                        ({
                            id,
                            kicker,
                            title,
                            body,
                            disclosure,
                            icon: Icon,
                            image,
                            coupleImage,
                            coupleCaption,
                            tags,
                            testId,
                            wide,
                            pageLink,
                        }) => (
                            <article
                                key={id}
                                data-testid={testId}
                                className={`group relative overflow-hidden rounded-2xl border border-[rgba(212,175,55,0.2)] bg-[#0b0e13] transition-all duration-500 hover:border-[rgba(212,175,55,0.45)] hover:shadow-[0_30px_60px_-30px_rgba(212,175,55,0.35)] ${
                                    wide ? "lg:col-span-2" : ""
                                }`}
                            >
                                {/* Image diptych: setting (wide) + couple (narrow) */}
                                <div className="relative grid h-72 w-full grid-cols-5 overflow-hidden md:h-[380px]">
                                    <div className="relative col-span-3 overflow-hidden">
                                        <img
                                            src={image}
                                            alt={`${title} — illustrative setting`}
                                            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                                            loading="lazy"
                                        />
                                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0b0e13]/70 via-transparent to-transparent" />
                                        <div className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full border border-[rgba(212,175,55,0.4)] bg-[#0b0e13]/70 px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] text-brand-gold2 backdrop-blur-md">
                                            <Icon
                                                aria-hidden="true"
                                                className="h-3.5 w-3.5"
                                            />
                                            {kicker}
                                        </div>
                                    </div>

                                    {/* Gold divider */}
                                    <div className="pointer-events-none absolute left-[60%] top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[rgba(212,175,55,0.45)] to-transparent" />

                                    <div className="relative col-span-2 overflow-hidden">
                                        <img
                                            src={coupleImage}
                                            alt={`${coupleCaption} — illustrative portrait`}
                                            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                                            loading="lazy"
                                        />
                                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0b0e13] via-[#0b0e13]/30 to-transparent" />
                                        <div
                                            data-testid={`${testId}-caption`}
                                            className="absolute bottom-3 left-3 right-3 text-[10px] uppercase tracking-[0.22em] text-brand-ivory/90"
                                        >
                                            {coupleCaption}
                                        </div>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="relative px-6 pb-8 pt-6 md:px-8 md:pb-10">
                                    <p className="text-[10px] uppercase tracking-[0.35em] text-brand-goldSoft">
                                        {kicker}
                                    </p>
                                    <h3 className="mt-3 font-serif text-2xl leading-tight text-brand-ivory md:text-3xl">
                                        {title}
                                    </h3>
                                    <p className="mt-4 text-base leading-relaxed text-brand-muted">
                                        {body}
                                    </p>
                                    <div className="mt-6 flex flex-wrap gap-2">
                                        {tags.map((t) => (
                                            <span
                                                key={t}
                                                className="rounded-full border border-[rgba(212,175,55,0.25)] bg-[rgba(212,175,55,0.04)] px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] text-brand-ivory"
                                            >
                                                {t}
                                            </span>
                                        ))}
                                    </div>
                                    {disclosure && (
                                        <p
                                            data-testid={`${testId}-disclosure`}
                                            className="mt-6 border-t border-[rgba(212,175,55,0.12)] pt-5 text-xs leading-relaxed text-brand-muted"
                                        >
                                            <span className="text-brand-goldSoft">
                                                Disclosure:{" "}
                                            </span>
                                            {disclosure}
                                        </p>
                                    )}
                                    {pageLink && (
                                        <Link
                                            to={pageLink}
                                            data-testid={`${testId}-readmore`}
                                            className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-brand-gold2 underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-sm"
                                        >
                                            Read more
                                            <ArrowRight
                                                aria-hidden="true"
                                                className="h-3 w-3 transition-transform group-hover:translate-x-1"
                                            />
                                        </Link>
                                    )}
                                </div>
                            </article>
                        )
                    )}
                </div>

                {/* Section-level product disclaimer */}
                <p
                    data-testid="regional-product-disclaimer"
                    className="mx-auto mt-10 max-w-3xl text-center text-xs leading-relaxed text-brand-muted md:mt-12"
                >
                    {DISCLAIMERS.product}
                </p>

                {/* Direct contact band */}
                <div
                    data-testid="regional-contact-band"
                    className="mt-10 overflow-hidden rounded-2xl border border-[rgba(212,175,55,0.3)] bg-gradient-to-r from-[#0b0e13] via-[#0d1117] to-[#0b0e13] md:mt-14"
                >
                    <div className="grid grid-cols-1 items-stretch md:grid-cols-3">
                        <div className="px-6 py-8 md:px-10 md:py-10">
                            <p className="text-[10px] uppercase tracking-[0.4em] text-brand-goldSoft">
                                Talk to a Licensed Agent
                            </p>
                            <h3 className="mt-3 font-serif text-2xl leading-tight text-brand-ivory md:text-3xl">
                                Have questions about a line of business?
                            </h3>
                            <p className="mt-2 text-sm text-brand-muted">
                                Reach us directly — we respond personally.
                            </p>
                        </div>

                        <EmailContact
                            variant="band"
                            testIdPrefix="band-email"
                        />

                        <a
                            data-testid="band-phone-link"
                            href={`tel:${COMPANY.phoneTel}`}
                            className="group relative flex items-center gap-4 border-t border-[rgba(212,175,55,0.2)] px-6 py-6 transition-colors hover:bg-[rgba(212,175,55,0.04)] md:border-l md:border-t-0 md:px-8 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg"
                        >
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[rgba(212,175,55,0.4)] bg-[#0d1117] transition-colors group-hover:border-brand-gold group-hover:bg-[rgba(212,175,55,0.08)]">
                                <Phone
                                    aria-hidden="true"
                                    className="h-5 w-5 text-brand-gold2"
                                />
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.35em] text-brand-goldSoft">
                                    Toll-Free
                                </p>
                                <p className="mt-1 font-serif text-xl text-brand-ivory transition-colors group-hover:text-brand-gold2 md:text-2xl">
                                    {COMPANY.phone}
                                </p>
                            </div>
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}
